import type { CarrierAccountInput, CarrierAccountView, CarrierTrackingCheck, CarrierTrackingInput, CarrierTrackingSettingsView, TenantSummary } from '@wms/domain';
import { useState } from 'react';
import { api, errorMessage } from '../../api';
import { carrierLabel, fmt } from '../../format';
import { useSession } from '../../session';
import { ago } from '../../tracking';
import { Empty, ErrorBanner, Modal, SellerChip, Spinner, useLoad, useToast } from '../../ui';
import { Icon } from '../../ui/icons';

const CARRIERS = ['aras', 'yurtici', 'mng', 'surat', 'ptt', 'ups', 'hepsijet', 'kolay_gelsin', 'sendeo'] as const;

const DRIVER_LABELS: Readonly<Record<CarrierAccountView['driver'], string>> = {
  manual: 'Entegrasyonsuz',
  tracking_pool: 'Takip no aralığı',
  carrier_api: 'Entegrasyon',
};

/**
 * Yönetim → Kargo anlaşmaları: the contracts parcels leave on when no marketplace ships them
 * (the site's and manual orders). The default warehouse contract carries every seller's
 * "depo anlaşması" orders; a seller's own contract its "kendi anlaşması" orders.
 */
export function Carriers() {
  const { me } = useSession();
  const admin = me.role === 'admin';
  const accounts = useLoad(() => api<CarrierAccountView[]>('GET', '/api/admin/carrier-accounts'), []);
  const tenants = useLoad(() => api<TenantSummary[]>('GET', '/api/admin/tenants'), []);
  const [editing, setEditing] = useState<CarrierAccountView | 'new' | null>(null);
  const [tracking, setTracking] = useState<CarrierAccountView | null>(null);

  return (
    <>
      <section className="card">
        <div className="card-h">
          <Icon name="truck" />
          <h2>Kargo anlaşmaları</h2>
          <span className="sub">Pazaryerinin kargosuyla gitmeyen siparişler (site, manuel, B2B) bu anlaşmalarla gider</span>
          <span className="spacer" style={{ flex: 1 }} />
          {admin && (
            <button className="btn primary" onClick={() => setEditing('new')}>
              <Icon name="plus" /> Yeni anlaşma
            </button>
          )}
        </div>
        <div className="card-b">
          <p className="muted" style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5 }}>
            <b>Entegrasyonsuz:</b> etiketi depo basar (gönderici, alıcı, sipariş numarası barkodu); kargo firması paketi teslim alırken kaydeder ve takip
            numarasını verir. <b>Takip no aralığı:</b> kargo firmasının size verdiği numaralar sırayla etikete basılır. Varsayılan anlaşma, kargo firması
            seçilmemiş siparişleri taşır. <b>Kargo takibi:</b> Aras anlaşmasında açılırsa takip numarası ve paketin durumu Aras’tan kendiliğinden gelir.
          </p>
        </div>
        {accounts.error && <ErrorBanner message={accounts.error} onRetry={() => void accounts.reload()} />}
        {!accounts.data ? (
          <Spinner />
        ) : accounts.data.length === 0 ? (
          <Empty title="Kargo anlaşması yok">Depo anlaşmalı siparişlerin etiketi basılamaz. {admin ? '“Yeni anlaşma” ile ekleyin.' : 'Depo yöneticisi eklemeli.'}</Empty>
        ) : (
          <div className="table-wrap">
            <table className="t stack-sm">
              <thead>
                <tr>
                  <th>Sahibi</th>
                  <th>Anlaşma</th>
                  <th>Yöntem</th>
                  <th className="hide-sm">Gönderici</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {accounts.data.map((a) => (
                  <tr key={a.id}>
                    <td>{a.tenantCode ? <SellerChip code={a.tenantCode} /> : <span className="chip info">Depo</span>}</td>
                    <td>
                      <b>{carrierLabel(a.carrierCode)}</b> · {a.label}
                      <div className="row" style={{ gap: 4, marginTop: 4 }}>
                        {a.isDefault && <span className="chip ok">Varsayılan</span>}
                        {!a.isActive && <span className="chip">Kapalı</span>}
                      </div>
                    </td>
                    <td>
                      {DRIVER_LABELS[a.driver]}
                      {a.carrierCode === 'aras' && <TrackingStatus tracking={a.tracking} />}
                      {a.trackingPool && (
                        <div className="muted mono" style={{ fontSize: 12.5 }}>
                          sıradaki {a.trackingPool.prefix}
                          {String(a.trackingPool.next).padStart(a.trackingPool.width, '0')} · kalan {fmt.n(Math.max(0, a.trackingPool.end - a.trackingPool.next + 1))}
                        </div>
                      )}
                    </td>
                    <td className="hide-sm">
                      {a.sender ? (
                        <>
                          {a.sender.name}
                          <div className="muted" style={{ fontSize: 12.5 }}>
                            {a.sender.address}
                            {a.sender.phone ? ` · ${a.sender.phone}` : ''}
                          </div>
                        </>
                      ) : (
                        <span className="chip warn">Gönderici yok</span>
                      )}
                    </td>
                    <td className="num">
                      <span className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                        {admin && a.carrierCode === 'aras' && (
                          <button className="btn sm" onClick={() => setTracking(a)}>
                            Kargo takibi
                          </button>
                        )}
                        {admin && a.driver !== 'carrier_api' && (
                          <button className="btn sm" onClick={() => setEditing(a)}>
                            Düzenle
                          </button>
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {editing && (
        <CarrierDialog
          account={editing === 'new' ? null : editing}
          tenants={(tenants.data ?? []).filter((t) => t.status !== 'offboarded')}
          firstForWarehouse={!(accounts.data ?? []).some((a) => a.tenantId === null)}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void accounts.reload();
          }}
        />
      )}
      {tracking && (
        <TrackingDialog
          account={tracking}
          onChanged={() => void accounts.reload()}
          onClose={() => {
            setTracking(null);
            void accounts.reload();
          }}
        />
      )}
    </>
  );
}

/** Kargo takibi of an Aras contract in the list: on and reading, failing, or off. */
function TrackingStatus({ tracking }: { tracking: CarrierTrackingSettingsView | null }) {
  if (!tracking) return <div className="muted" style={{ fontSize: 12.5 }}>Kargo takibi kapalı</div>;
  if (tracking.error) {
    return (
      <div className="trk-acct">
        <span className="chip bad">Kargo takibi çalışmıyor</span>
        <span className="muted">{tracking.error}</span>
      </div>
    );
  }
  return (
    <div className="trk-acct">
      <span className={`chip ${tracking.okAt ? 'ok' : 'info'}`}>Kargo takibi açık{tracking.test ? ' · test ortamı' : ''}</span>
      <span className="muted">{tracking.okAt ? `son okuma ${ago(tracking.okAt)}` : 'ilk okuma bekleniyor'}</span>
    </div>
  );
}

/**
 * Kargo takibi (Aras XML servisi) of an Aras contract, whatever its method: the login of Aras's
 * query service, sealed on the server and never shown again ("kayıtlı", the user name's last 3
 * characters). "Bağlantıyı dene" saves what was typed first, then asks Aras with it.
 */
function TrackingDialog(props: { account: CarrierAccountView; onChanged: () => void; onClose: () => void }) {
  const toast = useToast();
  const a = props.account;
  const [view, setView] = useState<CarrierTrackingSettingsView | null>(a.tracking);
  const [customerCode, setCustomerCode] = useState(a.tracking?.customerCode ?? '');
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [test, setTest] = useState(a.tracking?.test ?? false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [check, setCheck] = useState<CarrierTrackingCheck | null>(null);
  const saved = view?.saved === true;
  const loginTyped = userName.trim() !== '' || password !== '';
  const loginWhole = userName.trim() !== '' && password !== '';
  // a new login needs both; a saved one is kept while both stay blank
  const complete = customerCode !== '' && (saved ? !loginTyped || loginWhole : loginWhole);
  const dirty = loginTyped || customerCode !== (view?.customerCode ?? '') || test !== (view?.test ?? false);
  const url = `/api/admin/carrier-accounts/${a.id}/tracking`;

  const save = async () => {
    const body: CarrierTrackingInput = { customerCode, userName: userName.trim() || null, password: password || null, test };
    const next = await api<CarrierAccountView>('PUT', url, body);
    setView(next.tracking);
    setUserName('');
    setPassword('');
    props.onChanged();
  };

  const run = async (step: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await step();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const onSave = () =>
    run(async () => {
      await save();
      toast('Kargo takibi kaydedildi', 'ok');
      props.onClose();
    });

  const onCheck = () =>
    run(async () => {
      setCheck(null);
      if (dirty) await save();
      setCheck(await api<CarrierTrackingCheck>('POST', `${url}/check`));
      props.onChanged();
    });

  const onRemove = () => {
    if (!window.confirm('Kargo takibi kapatılsın mı? Kayıtlı Aras giriş bilgileri silinir.')) return;
    void run(async () => {
      await api('DELETE', url);
      toast('Kargo takibi kapatıldı', 'ok');
      props.onClose();
    });
  };

  return (
    <Modal
      title={`Kargo takibi · ${a.label}`}
      onClose={props.onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={props.onClose}>
            Vazgeç
          </button>
          <button className="btn" disabled={busy || !complete} onClick={() => void onCheck()}>
            <Icon name="plug" /> Bağlantıyı dene
          </button>
          <button className="btn primary" disabled={busy || !complete || !dirty} onClick={() => void onSave()}>
            Kaydet
          </button>
        </>
      }
    >
      <div className="stack">
        {error && <div className="banner bad">{error}</div>}
        <p className="muted" style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5 }}>
          Aras’ın XML servisine 15 dakikada bir bakılır: takip numarası siparişe yazılır, paketin durumu siparişte görünür. Sevkiyatta okutulmadan
          kuryeye verilen paket Aras kaydında görününce “kargoya verildi”, teslim edilince “teslim edildi” olur.
        </p>
        <div className="banner info-soft">
          <Icon name="info" />
          <span>
            Aras şubesine söyleyin: gönderiyi kaydederken etiketteki barkodu{a.driver === 'manual' ? ' (sipariş no)' : ''} <b>Müşteri Özel Kodu (MÖK)</b> alanına
            girsinler.
          </span>
        </div>
        <div>
          <b style={{ fontSize: 13.5 }}>Giriş bilgileri nereden alınır</b>
          <ol className="trk-steps">
            <li>
              <span className="mono">esasweb.araskargo.com.tr</span> adresine anlaşmanın hesabıyla girin.
            </li>
            <li>Entegrasyon → XML Servisleri sayfasını açın.</li>
            <li>Service Metod “GetQueryJSON” olan kullanıcının kullanıcı adını ve şifresini, müşteri kodunuzla birlikte aşağıya yazın.</li>
          </ol>
        </div>
        <div className="form-grid">
          <div className="field">
            <label htmlFor="trk-customer">Müşteri kodu</label>
            <input id="trk-customer" className="input mono" autoComplete="off" value={customerCode} onChange={(e) => setCustomerCode(e.target.value.trim())} />
          </div>
          <div className="field">
            <label htmlFor="trk-user">Kullanıcı adı {saved && <span className="muted">(kayıtlı{view?.userNameEnd ? ` …${view.userNameEnd}` : ''})</span>}</label>
            <input
              id="trk-user"
              className="input"
              autoComplete="off"
              placeholder={saved ? 'Değiştirmek için yazın' : ''}
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="trk-password">Şifre {saved && <span className="muted">(kayıtlı)</span>}</label>
            <input
              id="trk-password"
              className="input"
              type="password"
              autoComplete="new-password"
              placeholder={saved ? 'Değiştirmek için yazın' : ''}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
        </div>
        <label className="row" style={{ gap: 8 }}>
          <input type="checkbox" checked={test} onChange={(e) => setTest(e.target.checked)} />
          Test ortamı (Aras’ın verdiği test hesabıyla deneme)
        </label>
        {check && (
          <div className={`banner ${check.ok ? 'ok' : 'bad'}`}>
            <Icon name={check.ok ? 'check' : 'alert'} />
            <span>
              {check.message}
              {check.fields.length > 0 && (
                <span className="trk-fields">
                  Dönen alanlar:{' '}
                  {check.fields.map((f) => (
                    <span key={f} className="chip mono">
                      {f}
                    </span>
                  ))}
                </span>
              )}
            </span>
          </div>
        )}
        {!check && view && (view.error || view.okAt) && (
          <div className={`banner ${view.error ? 'bad' : 'ok'}`}>
            <Icon name={view.error ? 'alert' : 'check'} />
            <span>{view.error ? `Son okuma başarısız: ${view.error}` : `Son okuma ${ago(view.okAt!)}, sorunsuz`}</span>
          </div>
        )}
        {saved && (
          <div>
            <button className="btn sm ghost" disabled={busy} onClick={onRemove}>
              <Icon name="trash" size={16} /> Kargo takibini kapat
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}

function CarrierDialog(props: { account: CarrierAccountView | null; tenants: readonly TenantSummary[]; firstForWarehouse: boolean; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const a = props.account;
  const [tenantId, setTenantId] = useState(a?.tenantId ?? '');
  const [carrierCode, setCarrierCode] = useState(a?.carrierCode ?? 'aras');
  const [label, setLabel] = useState(a?.label ?? '');
  const [driver, setDriver] = useState<CarrierAccountInput['driver']>(a?.driver === 'tracking_pool' ? 'tracking_pool' : 'manual');
  const [senderName, setSenderName] = useState(a?.sender?.name ?? '');
  const [senderAddress, setSenderAddress] = useState(a?.sender?.address ?? '');
  const [senderPhone, setSenderPhone] = useState(a?.sender?.phone ?? '');
  const [prefix, setPrefix] = useState(a?.trackingPool?.prefix ?? '');
  const [next, setNext] = useState(a?.trackingPool ? String(a.trackingPool.next) : '');
  const [end, setEnd] = useState(a?.trackingPool ? String(a.trackingPool.end) : '');
  const [width, setWidth] = useState(a?.trackingPool ? String(a.trackingPool.width) : '');
  const [isDefault, setIsDefault] = useState(a ? a.isDefault : props.firstForWarehouse);
  const [isActive, setIsActive] = useState(a?.isActive ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const name = label.trim() || `${carrierLabel(carrierCode)} ${tenantId ? 'satıcı anlaşması' : 'depo anlaşması'}`;
  const digits = (v: string) => v.replace(/\D/g, '');

  const save = async () => {
    setBusy(true);
    setError(null);
    const body: CarrierAccountInput = {
      tenantId: tenantId || null,
      carrierCode,
      label: name,
      driver,
      sender: { name: senderName.trim(), address: senderAddress.trim(), phone: senderPhone.trim() || null },
      trackingPool:
        driver === 'tracking_pool'
          ? { prefix: prefix.trim().toUpperCase(), next: Number(next), end: Number(end), width: Number(width) || Math.max(next.length, end.length) }
          : null,
      isDefault,
      isActive,
    };
    try {
      await api(a ? 'PUT' : 'POST', a ? `/api/admin/carrier-accounts/${a.id}` : '/api/admin/carrier-accounts', body);
      toast('Kargo anlaşması kaydedildi', 'ok');
      props.onSaved();
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  return (
    <Modal
      title={a ? `${carrierLabel(a.carrierCode)} · ${a.label}` : 'Yeni kargo anlaşması'}
      onClose={props.onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={props.onClose}>
            Vazgeç
          </button>
          <button className="btn primary" disabled={busy || !senderName.trim() || !senderAddress.trim() || (driver === 'tracking_pool' && (!next || !end))} onClick={() => void save()}>
            Kaydet
          </button>
        </>
      }
    >
      <div className="stack">
        {error && <div className="banner bad">{error}</div>}
        <div className="form-grid">
          <div className="field">
            <label htmlFor="ca-owner">Anlaşmanın sahibi</label>
            <select id="ca-owner" className="select" value={tenantId} disabled={Boolean(a)} onChange={(e) => setTenantId(e.target.value)}>
              <option value="">Depo (depo anlaşmalı tüm siparişler)</option>
              {props.tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.code} · {t.displayName} (kendi anlaşması)
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="ca-carrier">Kargo firması</label>
            <select id="ca-carrier" className="select" value={carrierCode} onChange={(e) => setCarrierCode(e.target.value)}>
              {[...new Set([...CARRIERS, carrierCode])].map((c) => (
                <option key={c} value={c}>
                  {carrierLabel(c)}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="ca-label">Anlaşmanın adı</label>
            <input id="ca-label" className="input" placeholder={name} value={label} onChange={(e) => setLabel(e.target.value)} />
          </div>
        </div>

        <div className="field">
          <label>Yöntem</label>
          <label className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
            <input type="radio" name="ca-driver" checked={driver === 'manual'} onChange={() => setDriver('manual')} style={{ marginTop: 4 }} />
            <span>
              <b>Entegrasyonsuz</b>
              <span className="muted" style={{ display: 'block', fontSize: 13 }}>
                Etiketi depo basar: gönderici, alıcı ve sipariş numarası barkodu. Kargo firması paketi teslim alırken kaydeder, takip numarasını o verir.
              </span>
            </span>
          </label>
          <label className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
            <input type="radio" name="ca-driver" checked={driver === 'tracking_pool'} onChange={() => setDriver('tracking_pool')} style={{ marginTop: 4 }} />
            <span>
              <b>Takip numarası aralığı</b>
              <span className="muted" style={{ display: 'block', fontSize: 13 }}>Kargo firmasının size ayırdığı numaralar sırayla etikete basılır.</span>
            </span>
          </label>
        </div>
        {driver === 'tracking_pool' && (
          <div className="form-grid">
            <div className="field">
              <label htmlFor="ca-prefix">Önek (varsa)</label>
              <input id="ca-prefix" className="input mono" value={prefix} onChange={(e) => setPrefix(e.target.value.toUpperCase())} />
            </div>
            <div className="field">
              <label htmlFor="ca-next">Sıradaki numara</label>
              <input id="ca-next" className="input mono" inputMode="numeric" value={next} onChange={(e) => setNext(digits(e.target.value))} />
            </div>
            <div className="field">
              <label htmlFor="ca-end">Son numara</label>
              <input id="ca-end" className="input mono" inputMode="numeric" value={end} onChange={(e) => setEnd(digits(e.target.value))} />
            </div>
            <div className="field">
              <label htmlFor="ca-width">Hane sayısı</label>
              <input id="ca-width" className="input" inputMode="numeric" placeholder={String(Math.max(next.length, end.length) || '')} value={width} onChange={(e) => setWidth(digits(e.target.value))} />
            </div>
          </div>
        )}

        <div className="form-grid">
          <div className="field wide">
            <label htmlFor="ca-sender">Gönderici (etikette yazar)</label>
            <input id="ca-sender" className="input" placeholder="Firma unvanı" value={senderName} onChange={(e) => setSenderName(e.target.value)} />
          </div>
          <div className="field wide">
            <label htmlFor="ca-address">Gönderici adresi</label>
            <input id="ca-address" className="input" placeholder="Depo adresi, ilçe / il" value={senderAddress} onChange={(e) => setSenderAddress(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="ca-phone">Gönderici telefonu</label>
            <input id="ca-phone" className="input" inputMode="tel" value={senderPhone} onChange={(e) => setSenderPhone(e.target.value)} />
          </div>
        </div>

        <label className="row" style={{ gap: 8 }}>
          <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
          Varsayılan anlaşma: kargo firması seçilmemiş {tenantId ? 'kendi anlaşmalı' : 'depo anlaşmalı'} siparişler bununla gider
        </label>
        <label className="row" style={{ gap: 8 }}>
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          Açık
        </label>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Kaydedince, anlaşma olmadığı için etiketi basılamayan açık siparişlerin etiketi yeniden hazırlanır.
        </p>
      </div>
    </Modal>
  );
}
